import React, { useEffect } from "react";
import ReactDOM from "react-dom";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";

import useSetState from "../../hooks/useSetState";
import NavItem from "./NavItem";
import KCustomerSelector from "../customerSelect";
import KLogo from "../ui/KLogo";
import { getCustomers, refreshToken } from "../../apiCalls/auth";
import {
  setCustomer,
  setCustomers,
  resetUser,
  resetAutosuggestState,
} from "../../redux/actions/actions";
import { defaultCustomer } from "../constants/defaultCustomer";

import "./Navigation.css";
import { RootStateInterface } from "../../interfaces/ReduxInterface";

export const DEFAULT_ROUTE = "/admin/settings/general";

const Navigation: React.FC = () => {
  const { CancelToken } = axios;
  const [{ search, cancelTokenSource, customerFilters }, setState] =
    useSetState({
      search: "",
      cancelTokenSource: null,
      customerFilters: "archived:ne:true",
    });
  const handleChange = (e) => setState({ [e.target.name]: e.target.value });
  const customerFromLocalStorage = localStorage.getItem("currentCustomer");

  let currentCustomer;
  try {
    currentCustomer = JSON.parse(customerFromLocalStorage!);
  } catch (error) {
    console.error("Error parsing JSON from localStorage:", error);
    currentCustomer = { currentCustomer: null };
  }

  const allCustomer = useSelector(
    (state: RootStateInterface) => state.customers.customers,
  );
  const dispatch = useDispatch();

  const options = allCustomer.filter((el) => !!el._id);

  const isTheCurrentCustomerDefault =
    currentCustomer?.name === defaultCustomer.name;
  const getDefaultCustomer = (customers) => {
    const kyburzCustomer = customers.find((c) => c.shortname === "kyburz");
    return kyburzCustomer || customers[0];
  };

  const handleCustomerChange = (customer) => {
    localStorage.setItem("currentCustomer", JSON.stringify(customer));
    dispatch(setCustomer(customer));
    dispatch(resetUser());
    dispatch(resetAutosuggestState());
  };

  const loadCustomers = (query, appliedFilters?) => {
    if (cancelTokenSource !== null) {
      cancelTokenSource.cancel("Search cancelled");
    }
    const source = CancelToken.source();
    setState({ cancelTokenSource: source });

    getCustomers(query, source.token, appliedFilters)
      .then((res) => {
        if (res.data) {
          dispatch(setCustomers(res.data));

          if (
            (!currentCustomer || isTheCurrentCustomerDefault) &&
            res.data.length
          ) {
            handleCustomerChange(getDefaultCustomer(res.data));
          }
        }
      })
      .catch((e) => {
        setState({
          cancelToken: null,
        });
        console.error(e);
      });
  };

  const setCustomerFilters = (showArchived: boolean) => {
    const filters = showArchived ? undefined : "archived:ne:true";
    setState({ customerFilters: filters });
  };

  useEffect(() => {
    loadCustomers(search, customerFilters);
  }, [search, customerFilters]);

  useEffect(() => {
    refreshToken();
  }, []);

  const adminLinks = [
    // { name: "Fleet", icon: "truck", route: "/admin/fleet/vehicles" },
    // { name: "Drivers", icon: "users", route: "/admin/drivers/" },
    // { name: "Users", icon: "users", route: "/admin/users/users/" },
    {
      name: "Org Settings",
      icon: "settings",
      route: "/admin/settings/general",
    }
  ];

  const configLinks = [
    { name: "Devices", icon: "cpu", route: "/config/devices/devices" },
    {
      name: "IoT Key Devices",
      icon: "radio",
      route: "/config/iot_key_devices/devices",
    },
    {
      name: "Templates",
      icon: "clipboard",
      route: "/config/templates/basicTemplates",
    },
    {
      name: "Vehicle templates",
      icon: "clipboard",
      route: "/config/vehicle/templates/templates",
    },
    {
      name: "Report configs (v2)",
      icon: "clipboard",
      route: "/config/report_configs",
    },
    {
      name: "SMS commands",
      icon: "message-square",
      route: "/config/sms",
    },
    {
      name: "IoT Key commands",
      icon: "radio",
      route: "/config/iot_commands",
    },
  ];

  const billingLinks = [
    { name: "Billing", icon: "credit-card", route: "/admin/billing/invoices" },
  ];

  const navigation = (
    <div className="navigation__background">
      <div className="navigation__logo">
        <KLogo />
      </div>

      <div className="navigation__admin-container">
        <p className="navigation__section-title">Admin</p>
        <div className="navigation__customer-select">
          <KCustomerSelector
            options={options}
            customer={currentCustomer}
            search={search}
            handleChange={handleChange}
            handleCustomerChange={handleCustomerChange}
            handleArchivedToggle={setCustomerFilters}
          />
        </div>
        <ul>
          {adminLinks.map((el) => (
            <NavItem
              name={el.name}
              icon={el.icon}
              route={el.route}
              key={el.route}
            />
          ))}
        </ul>
      </div>

      <div className="navigation__config-container">
        <p className="navigation__section-title">Config</p>
        <ul>
          {configLinks.map((el) => (
            <NavItem
              name={el.name}
              icon={el.icon}
              route={el.route}
              key={el.route}
            />
          ))}
        </ul>
      </div>

      <div className="navigation__billing-container">
        <p className="navigation__section-title">Internal Billing</p>
        <ul>
          {billingLinks.map((el) => (
            <NavItem
              name={el.name}
              icon={el.icon}
              route={el.route}
              key={el.route}
            />
          ))}
        </ul>
      </div>
    </div>
  );

  return ReactDOM.createPortal(
    navigation,
    document.getElementById("nav-hook") as HTMLElement,
  );
};

export default Navigation;
